import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { CustomerTag } from '../../lib/customers';
import {
  RULE_KINDS,
  emptyRule,
  tagsForBusiness,
  type CustomerSegment,
  type SegmentRule,
} from '../../lib/customers/segments';

export default function SegmentEditor({
  businessId,
  segment,
  hiddenActive,
  onShowActive,
  onClose,
  onSave,
  onDelete,
}: {
  businessId: string;
  segment: CustomerSegment | null;
  hiddenActive: CustomerSegment | null;
  onShowActive: () => void;
  onClose: () => void;
  onSave: (input: { name: string; isLeadList: boolean; visible?: boolean; rules: SegmentRule[] }) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const system = segment?.system_key || null;
  const locked = system === 'customers' || system === 'all_contacts';
  const active = system === 'active_customers';
  const [name, setName] = useState(segment?.name || '');
  const [leadList, setLeadList] = useState(segment?.is_lead_list || false);
  const [visible, setVisible] = useState(segment?.visible !== false);
  const [rules, setRules] = useState<SegmentRule[]>(
    segment?.system_key === 'active_customers'
      ? (segment.rules.length ? segment.rules : [emptyRule('invoice_within_days')])
      : (segment?.rules?.length ? segment.rules : [emptyRule()]),
  );
  const [tags, setTags] = useState<CustomerTag[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    tagsForBusiness(businessId).then(setTags).catch(() => setTags([]));
  }, [businessId]);

  const setRule = (index: number, next: SegmentRule) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? next : rule)));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: active || locked ? segment!.name : name,
        isLeadList: locked || active ? false : leadList,
        visible: active ? visible : undefined,
        rules: locked ? [] : rules,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save this segment.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:max-w-lg sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{segment ? segment.name : 'New segment'}</h2>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-gray-400 hover:bg-gray-100" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!segment && hiddenActive && (
          <button
            type="button"
            onClick={onShowActive}
            className="mb-4 w-full rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-left text-sm font-medium text-emerald-800"
          >
            Show Active customers on this list
          </button>
        )}

        {locked && (
          <p className="text-sm text-gray-600">
            {system === 'customers'
              ? 'Customers are people you added yourself, plus anyone who has booked or been invoiced. People who only sit on a lead list stay out of this one until they do.'
              : 'All contacts is everyone who has not been archived, including lead lists.'}
          </p>
        )}

        {active && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">A customer is active when they have an invoice, paid or unpaid, in this window. Bookings, orders, and product sales count.</p>
            <label className="flex items-center justify-between text-sm font-medium text-gray-800">
              Show on the customer list
              <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
            </label>
            <label className="block text-sm font-medium text-gray-800">
              Days
              <input
                type="number"
                min={1}
                max={3650}
                value={Number(rules[0]?.value?.days) || 45}
                onChange={(e) => setRule(0, { ...emptyRule('invoice_within_days'), value: { days: Math.max(1, Number(e.target.value) || 45) }, operator: 'has' })}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2"
              />
            </label>
          </div>
        )}

        {!locked && !active && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-800">
              Name
              <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2" placeholder="Kelowna shops" />
            </label>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" className="mt-1" checked={leadList} onChange={(e) => setLeadList(e.target.checked)} />
              <span>Lead list. People on it do not count as customers until they book or get an invoice.</span>
            </label>
            <div className="space-y-2">
              {rules.map((rule, index) => (
                <div key={index} className="rounded-xl border border-gray-200 p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <select
                      value={rule.kind}
                      onChange={(e) => setRule(index, emptyRule(e.target.value))}
                      className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                    >
                      {RULE_KINDS.map((kind) => (
                        <option key={kind.id} value={kind.id}>{kind.label}</option>
                      ))}
                    </select>
                    {rules.length > 1 && (
                      <button type="button" className="text-xs text-gray-400" onClick={() => setRules((prev) => prev.filter((_, i) => i !== index))}>Remove</button>
                    )}
                  </div>
                  <RuleFields rule={rule} tags={tags} onChange={(next) => setRule(index, next)} />
                </div>
              ))}
              <button type="button" className="text-sm font-medium text-blue-600" onClick={() => setRules((prev) => [...prev, emptyRule()])}>
                Add condition
              </button>
              <p className="text-xs text-gray-400">Everyone in the segment matches every condition.</p>
            </div>
          </div>
        )}

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        {!locked && (
          <div className="mt-5 flex items-center justify-between gap-2">
            {segment && onDelete ? (
              <button type="button" className="text-sm text-red-600" onClick={() => void onDelete()} disabled={saving}>Delete</button>
            ) : <span />}
            <button type="button" onClick={() => void save()} disabled={saving} className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {saving ? 'Saving…' : 'Save segment'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RuleFields({
  rule,
  tags,
  onChange,
}: {
  rule: SegmentRule;
  tags: CustomerTag[];
  onChange: (rule: SegmentRule) => void;
}) {
  if (rule.kind === 'tags') {
    const selected = Array.isArray(rule.value.tagIds) ? rule.value.tagIds as string[] : [];
    return (
      <div className="space-y-2">
        <select value={rule.operator} onChange={(e) => onChange({ ...rule, operator: e.target.value })} className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
          <option value="includes">Includes</option>
          <option value="excludes">Does not include</option>
        </select>
        <div className="flex flex-wrap gap-1.5">
          {tags.length === 0 && <span className="text-xs text-gray-400">No tags yet.</span>}
          {tags.map((tag) => {
            const on = selected.includes(tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => onChange({
                  ...rule,
                  value: { tagIds: on ? selected.filter((id) => id !== tag.id) : [...selected, tag.id] },
                })}
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${on ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                {tag.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (rule.kind === 'contact') {
    return (
      <select value={rule.operator} onChange={(e) => onChange({ ...rule, operator: e.target.value })} className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
        <option value="has_email">Has an email</option>
        <option value="missing_email">Missing an email</option>
        <option value="has_phone">Has a phone</option>
        <option value="missing_phone">Missing a phone</option>
      </select>
    );
  }
  if (rule.kind === 'customer_type') {
    return (
      <select value={String(rule.value.type || 'person')} onChange={(e) => onChange({ ...rule, value: { type: e.target.value } })} className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
        <option value="person">Person</option>
        <option value="business">Business</option>
      </select>
    );
  }
  if (rule.kind === 'is_supplier') {
    return (
      <select value={rule.operator} onChange={(e) => onChange({ ...rule, operator: e.target.value })} className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
        <option value="is">Is a supplier</option>
        <option value="is_not">Is not a supplier</option>
      </select>
    );
  }
  if (rule.kind === 'booking' || rule.kind === 'ever_invoice' || rule.kind === 'ever_booking') {
    return (
      <div className="flex gap-2">
        <select value={rule.operator} onChange={(e) => onChange({ ...rule, operator: e.target.value })} className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
          <option value="has">Has</option>
          <option value="has_not">Does not have</option>
        </select>
        {rule.kind === 'booking' && (
          <input
            type="number"
            min={1}
            className="w-24 rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
            value={Number(rule.value.days) || 90}
            onChange={(e) => onChange({ ...rule, value: { days: Math.max(1, Number(e.target.value) || 1) } })}
          />
        )}
        {rule.kind === 'booking' && <span className="self-center text-xs text-gray-400">days</span>}
      </div>
    );
  }
  if (rule.kind === 'invoice_within_days' || rule.kind === 'created_within_days') {
    return (
      <div className="flex items-center gap-2">
        {rule.kind === 'invoice_within_days' && (
          <select value={rule.operator} onChange={(e) => onChange({ ...rule, operator: e.target.value })} className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
            <option value="has">Has</option>
            <option value="has_not">Does not have</option>
          </select>
        )}
        <input
          type="number"
          min={1}
          className="w-24 rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          value={Number(rule.value.days) || 30}
          onChange={(e) => onChange({ ...rule, value: { days: Math.max(1, Number(e.target.value) || 1) } })}
        />
        <span className="text-xs text-gray-400">days</span>
      </div>
    );
  }
  if (rule.kind === 'amount_spent') {
    return (
      <div className="flex gap-2">
        <select value={rule.operator} onChange={(e) => onChange({ ...rule, operator: e.target.value })} className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
          <option value="at_least">At least</option>
          <option value="under">Under</option>
        </select>
        <input
          type="number"
          min={0}
          className="w-28 rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          value={Number(rule.value.amount) || 0}
          onChange={(e) => onChange({ ...rule, value: { amount: Math.max(0, Number(e.target.value) || 0) } })}
        />
      </div>
    );
  }
  return (
    <input
      value={String(rule.value.text || '')}
      onChange={(e) => onChange({ ...rule, value: { text: e.target.value } })}
      placeholder={rule.kind === 'address_contains' ? 'Kelowna' : 'Referral'}
      className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
    />
  );
}
